import { ComponentInformation, CompositionInformation } from 'json-react-layouts'
import { createDiagExample } from 'component-library/diagnostics/createDiagExample'
import { PageRouteInformation, StaticRoutes } from 'common/routing/page-definition'
import { Section } from 'component-library/__styling/settings/sections'
import { createHeading } from 'common/client/helpers/block-helpers'
import { ListBlock, SubheadBlock } from 'common/client-data-types/blocks'

type ErrorRouteComponents =
    | ComponentInformation<'component-renderer-error-test', any>
    | ComponentInformation<'fail-load-content', any>
    | ComponentInformation<'fail-load-content-404', any>

// needs to be box as all products have it imported
type ErrorRouteCompositions = CompositionInformation<'box', ErrorRouteComponents, 'main', any>

type ErrorPageType = PageRouteInformation<Section, ErrorRouteComponents, ErrorRouteCompositions>

export const diagFailResolveRoute = (path: string) => {
    // matched path so regionals work / sevennews sections work
    if (new RegExp(`${errorRoutes.routeInfoFailed.route}$`).test(path)) {
        throw new Error('resolveRoute test failure')
    }
    if (new RegExp(`${errorRoutes.routeInfoFailed400.route}$`).test(path)) {
        throw new Error('400')
    }
    return
}

export const errorRoutes = {
    missingPage: {
        route: '/diag/404',
        info: 'Example of when a page cannot be found',
    },
    missingData: {
        route: '/diag/content-404',
        info: 'Example of when a page cannot load data',
    },
    dataLoaderError: {
        route: '/diag/500_component-data-loader-error',
        info: 'Example of when a component fails to load data',
    },
    componentRenderError: {
        route: '/diag/500_component-render-error',
        info: 'Example of when a page fails to render due to component error (React)',
    },
    routeInfoFailed: {
        route: '/diag/500_route-info-resolver-failure',
        info: 'Example of when the route info resolver fails (ie. content api calls fail)',
    },
    routeInfoFailed400: {
        route: '/diag/400_route-info-resolver-failure',
        info: 'Example when url causes a content API bad request',
    },
}

const missingDataErrorPage: ErrorPageType = {
    kind: 'page',
    heading: 'Renders failure text, no data loaded',
    pageType: 'static',
    section: 'default',
    adTargeting: {
        pageId: 'fail-data-load',
        adUnitPath: '',
        ssAdUnits: [],
        topics: [],
    },
    compositions: [
        {
            type: 'box',
            props: {},
            contentAreas: {
                main: [
                    {
                        type: 'fail-load-content-404',
                        props: {
                            dataDefinitionArgs: {},
                        },
                    },
                ],
            },
        },
    ],
}

const dataLoaderErrorPage: ErrorPageType = {
    kind: 'page',
    heading: 'Renders failure text, no data loaded',
    pageType: 'static',
    section: 'default',
    adTargeting: {
        pageId: 'fail-data-load',
        adUnitPath: '',
        ssAdUnits: [],
        topics: [],
    },
    compositions: [
        {
            type: 'box',
            props: {},
            contentAreas: {
                main: [
                    {
                        type: 'fail-load-content', // renders failure text
                        props: {
                            dataDefinitionArgs: {},
                        },
                    },
                ],
            },
        },
    ],
}

const componentRenderErrorPage: ErrorPageType = {
    kind: 'page',
    heading: 'Failed to render page',
    pageType: 'static',
    section: 'default',
    adTargeting: {
        pageId: 'failed-render',
        adUnitPath: '',
        ssAdUnits: [],
        topics: [],
    },
    compositions: [
        {
            type: 'box',
            props: {},
            contentAreas: {
                main: [
                    {
                        type: 'component-renderer-error-test',
                        props: {},
                    },
                ],
            },
        },
    ],
}

/** You need to register `fail-data-load`, `fail-data-load-404` and `component-renderer-error-test` components */
// Used for regionals to match diag pages
export const matchedErrorRoutePages: StaticRoutes<any, any> = {
    [`${errorRoutes.missingData.route}$`]: () => missingDataErrorPage,
    [`${errorRoutes.dataLoaderError.route}$`]: () => dataLoaderErrorPage,
    [`${errorRoutes.componentRenderError.route}$`]: () => componentRenderErrorPage,
}

const filterErrorRoutes = (type: '404' | '!404') => {
    return Object.keys(errorRoutes).filter(routeKey => {
        const route = (errorRoutes as any)[routeKey].route
        if (type === '404') {
            return route.indexOf('404') > -1
        }
        return route.indexOf('404') === -1
    })
}

// Make it relative for the page, to allow it to work with sectioned pages
// Cant be relative for tests
function makeRelativeLink(routeLink: string) {
    if (routeLink.indexOf('/') === 0 && process.env.NODE_ENV !== 'test') {
        return routeLink.substr(1, routeLink.length - 1)
    }
    return routeLink
}

// Error Routes list split up by expected errors
export const errorRoutesList: Array<ListBlock | SubheadBlock> = [
    createHeading('Page Not Found Errors'),
    {
        kind: 'ordered-list',
        items: [
            ...filterErrorRoutes('404').map(routeKey =>
                createDiagExample(
                    makeRelativeLink((errorRoutes as any)[routeKey].route),
                    (errorRoutes as any)[routeKey].info
                )
            ),
        ],
    },
    createHeading('Something Went Wrong Errors'),
    {
        kind: 'ordered-list',
        items: [
            ...filterErrorRoutes('!404').map(routeKey =>
                createDiagExample(
                    makeRelativeLink((errorRoutes as any)[routeKey].route),
                    (errorRoutes as any)[routeKey].info
                )
            ),
        ],
    },
]
